import { useEffect, useState } from "react";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { DataTableColumn } from "../../../../components/common/datatable/DataTableTypes";
import { createClickableLink, SearchSelectWithIndeterminateBoolean } from "../../../../components/common/datatable/DataTableUtils";
import { SearchInputWrapper } from "../../../../components/common/datatable/filter/SearchInputWrapper";
import { SearchSelectWithEnum, SearchSelectWithList } from "../../../../components/common/datatable/filter/SearchSelectWrapper";
import { getEnumDisplayNameByConstant } from "../../../../helpers/utils/EnumUtils";
import { formatValueAsDateAndTime } from "../../../../helpers/utils/DateUtils";
import { SearchDatePickerWrapper } from "../../../../components/common/datatable/filter/SearchDatePickerWrapper";
import Breadcrumb from "../../../../components/common/Breadcrumbs";
import { useLazyGetAllStatesQuery } from "../../../data/states/statesApi";
import { useGetAllCustomersListQuery, useGetParentAllCustomerQuery } from "../../../customer/customerApi";
import { useLazyRegionByCustomerIdQuery } from "../../../customer/regionApi";
import { DataTable } from "../../../../components/common/datatable/DataTable";
import { useLazyExportTurnAroundReportDataQuery, useSearchTurnAroundReportApiQuery } from "../report";
import ReportType from "./ReportType";
import { useFindUserSettingsByGroupNameQuery } from "../../../../components/common/userSetting/userSettingsApi";
import { getCurrentMonthStartDate } from "../../../../helpers/utils/Utils";

const TurnAroundReport = () => {
    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "Turn Around Report", url: "" }
    ]);

    const [stateDataApi, stateDataApiResult] = useLazyGetAllStatesQuery();
    const customerTenantApi = useGetAllCustomersListQuery("");
    const { data: userSetting } = useFindUserSettingsByGroupNameQuery("Report Turn Around");
    const [type, setType] = useState('TURN_AROUND');
    const [triggerExportReportDataQuery] = useLazyExportTurnAroundReportDataQuery();
    const parentCustomerList = useGetParentAllCustomerQuery("");
    const { data: enums } = useGetEnumsQuery("");
    const [getAllRegionDataApi, getAllRegionDataApiResponse] = useLazyRegionByCustomerIdQuery();
    const [selectedStateId, setSelectedStateId] = useState(null);
    const [disableIssued, setDisableIssued] = useState(false);
    const formattedStartDate = getCurrentMonthStartDate();

    useEffect(() => {
        document.title = 'Reports';
    }, []);

    const UpdateInclusionList = [
        { constant: "All", value: "All" },
        { constant: "Active", value: "Active" },
        { constant: "Original", value: "Original" },
    ];

    const columns: DataTableColumn[] = [
        {
            label: "Order No.",
            accessor: "customerOrderNumber",
            canSort: false,
            cell: (props: any) => {
                let path = "/orders/" + props.row.original.orderId;
                return createClickableLink(path, props.value, userSetting, false, "", ["ORDR-R"]);
            },
            filterOptions: [
                {
                    filterComponent: <SearchInputWrapper type="text" placeholder="Enter Order No..." name="customerOrderNumber" accessor="customerOrderNumber" />
                }
            ]
        },
        {
            label: "Customer",
            accessor: "customerName",
            canSort: false,
            filterOptions: [
                customerTenantApi?.data && {
                    filterComponent: SearchSelectWithList(
                        customerTenantApi?.data,
                        (customer: { customerName: any; customerNumber: any; }) => `${customer.customerName} (${customer.customerNumber})`,
                        "id",
                        "Select Customer...",
                        false,
                        "customerId"
                    ),
                    overrideFilterAccessor: "customerId"
                }
            ].filter(Boolean)
        },
        {
            label: "Parent Customer",
            accessor: "parentCustomerName",

            canSort: false,
            filterOptions: [{
                dependentColumns: ["Region State", "Region"],
                filterComponent: SearchSelectWithList(parentCustomerList?.data,
                    (customer: any) => `${customer.customerName} (${customer.customerNumber})`,
                    "id", "Select Parent Customer...", false, "parentCustomerId"),
                overrideFilterAccessor: "parentCustomerId"
            }]
        },
        {
            label: "Region State", accessor: "regionStateId", canSort: false,
            filterOptions: [{

                filterComponent: SearchSelectWithList(stateDataApiResult?.data, "stateName", "id", "Select Region State...", false, "regionStateId", null),
                onDependencyUpdated: (value: any) => {
                    if (value) {
                        stateDataApi(value);
                    }
                },
            }],
            visibility: { canColumnShow: true, showByDefault: true }
        },
        {
            label: "Region", accessor: "region", canSort: false, filterOptions: [{
                filterComponent: SearchSelectWithList(Array.isArray(getAllRegionDataApiResponse?.data) ? (selectedStateId ? getAllRegionDataApiResponse?.data?.filter(region => region.stateId === selectedStateId) : getAllRegionDataApiResponse?.data) : [], "name", "id", "Select Region...", false, "region", null),
                onDependencyUpdated: (value: any) => {
                    getAllRegionDataApi(value)
                },
            }],
            visibility: { canColumnShow: false, showByDefault: false }
        },
        {
            label: "Product(s)",
            accessor: "products",
            canSort: false,
            cell: (props: any) => {
                let content = props.row.original.products.map((cur: any) => {
                    return getEnumDisplayNameByConstant(enums?.["ProductType"], cur);
                }).join(", ");
                return <span className="text_wrap_msa_grid" title={content}>{content}</span>;
            },
            filterOptions: [
                enums?.["ProductType"] && {
                    filterComponent: SearchSelectWithEnum(enums?.["ProductType"], "Select Product Type(s)...", true, "productTypes"),
                    overrideFilterLabel: "Product Type(s)",
                    overrideFilterAccessor: "productTypes"
                }
            ].filter(Boolean)
        },
        {
            label: "Created",
            accessor: "creationDate",
            canSort: false,
            cell: (props: any) => props.value ? formatValueAsDateAndTime(props.value, "MM/D/YYYY") : '',
            filterOptions: [
                {
                    filterComponent: <SearchDatePickerWrapper accessor="creationDateFrom" />,
                    overrideFilterAccessor: "creationDateFrom",
                    overrideFilterLabel: "Creation Date - From"
                },
                {
                    filterComponent: <SearchDatePickerWrapper accessor="creationDateTo" />,
                    overrideFilterAccessor: "creationDateTo",
                    overrideFilterLabel: "Creation Date - To"
                }
            ]
        },
        {
            label: "Issued",
            accessor: "issuedDate",
            canSort: false,
            overrideSortColumn: "deliveredDate",
            cell: (props: any) => props.value ? formatValueAsDateAndTime(props.value, "MM/D/YYYY") : '',
            filterOptions: [
                {
                    filterComponent: <SearchDatePickerWrapper accessor="deliveredDateFrom" disabled={disableIssued} />,
                    onDependencyUpdated: (value: any) => setDisableIssued(!!value),
                    overrideFilterAccessor: "deliveredDateFrom",
                    overrideFilterLabel: "Delivered Date - From"
                },
                {
                    filterComponent: <SearchDatePickerWrapper accessor="deliveredDateTo" disabled={disableIssued} />,
                    overrideFilterAccessor: "deliveredDateTo",
                    overrideFilterLabel: "Delivered Date - To"
                }
            ]
        },
        {
            label: "Age",
            accessor: "age",
            overrideSortColumn: "age",
            visibility: { canColumnShow: true, showByDefault: true },
            canSort: false,
            cell: (props: any) => {
                return props.value;
            }
        },
        {
            label: "Turn Time",
            accessor: "daysOpen",
            overrideSortColumn: "daysOpen",
            visibility: { canColumnShow: true, showByDefault: true },
            canSort: false,
            cell: (props: any) => {
                return props.value;
            }
        },
        {
            label: "Update Inclusion",
            accessor: "updateInclusion",
            visibility: { canColumnShow: true, showByDefault: false },
            filterOptions: [{
                filterComponent: SearchSelectWithEnum(UpdateInclusionList, "Select Update Inclusion...", false, "updateInclusion", "ALL"),
            }],
        },
        {
            label: "Include Open Orders",
            accessor: "includeOpenOrders",
            visibility: { canColumnShow: true, showByDefault: false },
            filterOptions: [{
                dependentColumns: ["Issued"],
                filterComponent: SearchSelectWithIndeterminateBoolean("Is Include Open Orders...", "includeOpenOrders"),
            }]
        },
        {
            label: "Follow Up Info",
            accessor: "followUps",
            canSort: false,
            cell: (props: any) => {
                return props.value;
            }
        },


    ];

    const customFilter = {
        updateInclusion: "All",
        includeOpenOrders: false,
        creationDateFrom: formattedStartDate
    }
    return (
        <div className="user-container px-4 py-2">
            <div className="flex justify-between">
                <Breadcrumb crumbs={crumbs} />
            </div>
            <ReportType type={type} setType={setType} />
            <DataTable
                columns={columns}
                actions={{
                    fetchAction: useSearchTurnAroundReportApiQuery,
                    exportAction: triggerExportReportDataQuery
                }}
                defaultFilter={
                    {
                        ...customFilter,
                        "sortOrder": "ASCENDING",
                        "pageNumber": 0,
                        "pageSize": "15",
                        "sort": "creationDate"
                    }
                }
                userSettingGroup={"Report Turn Around"}
                basePermission={"TRNREPO"}
            />
        </div>
    );
}

export default TurnAroundReport;
